// PlacePalletModal — put ONE pallet somewhere: a cell, or (still) the room's
// overflow. The way back OUT of the overflow, the way to fill in a pallet whose
// cell was never recorded, and — since PB-11 — the way to MOVE a pallet that is
// on a cell, from the Coldroom drawer (PM-CST-05). Moving one used to mean the
// manager-only Loading Bay map, via Back → Home → tile.
//
// Its own file rather than a fourth local component inside Coldroom.tsx, which
// is already an 891-line page — and because the write below is the interesting
// half and deserves to be reachable by a test without rendering a whole floor
// map.
//
// A single version-guarded UPDATE rather than an RPC. There is no batch to keep
// atomic here (one pallet, one row) and the two guards that matter are already
// on the table: the partial unique index refuses a taken cell, and
// check_cell_occupancy_for_pallet refuses a cell holding bins. Both surface as
// 23505/23P01, which coldroomCellCollisionMessage turns into one sentence.

import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Layers } from 'lucide-react'
import CellPicker from '@/components/CellPicker'
import DestinationRoomTabs, { type RoomTab } from '@/components/DestinationRoomTabs'
import Modal from '@/components/Modal'
import { isStaleWriteError, updateWithVersion } from '@/lib/optimistic'
import { coldroomCellCollisionMessage } from '@/lib/floorMapMove'
import { ValidationError } from '@/lib/errorReporter'
import { withReadTimeout, withTimeout } from '@/lib/saveTimeout'
import { supabase } from '@/lib/supabase'
import { transitionPallet } from '@/lib/palletTransition'
import { useCurrentUser } from '@/lib/useCurrentUser'
import {
  placementPatch, placementIsComplete, placementLabel,
  OVERFLOW_LABEL, type ColdroomPlacement,
} from '@/lib/coldroomPlacement'

/** Just enough of a pallet to move it and to say where it is now. */
export interface PlaceablePallet {
  pallet_id:         string
  version:           number
  coldroom_id:       string | null
  coldroom_overflow: boolean
  /** Set when the pallet is ON a cell: the sheet is then a MOVE, says so, and
   *  records it as one. */
  coldroom_grid?:    string | null
  /** 'IN_PRODUCTION' turns the sheet into a first placement from the
   *  production side (F05 UF-01): the write goes through transitionPallet(),
   *  which moves the status as well as the cell. Anything else is a re-place. */
  status?:           string
  awaiting_weight?:  boolean | null
  /** A received (EXTERNAL_RECEIPT) pallet skips the weigh rule. */
  skipWeighGate?:    boolean
}

// The write. Separated from the component because the two failures it has to
// translate — someone else got there first, and the cell is taken — are the
// same two the floor-map move already translates, and neither is anything to do
// with rendering a sheet. Not exported: its sibling test drives it through the
// sheet, which is the only way it is ever reached, and an export with no
// importer is a promise of a shared contract that does not exist.
async function placeOffGridPallet(
  pallet: PlaceablePallet,
  destRoomId: string,
  dest: ColdroomPlacement,
  meId: string | undefined,
): Promise<void> {
  try {
    if (pallet.status === 'IN_PRODUCTION') {
      if (!meId) throw new ValidationError('Not authenticated')
      await withTimeout(transitionPallet({
        pallet: { pallet_id: pallet.pallet_id, version: pallet.version, awaiting_weight: pallet.awaiting_weight },
        toStatus: 'IN_COLDROOM',
        extra: placementPatch(destRoomId, dest),
        meId,
        skipWeighGate: pallet.skipWeighGate,
      }), 'Coldroom: place pallet from production')
      return
    }
    // placementPatch is what keeps "on a cell" and "in the overflow" mutually
    // exclusive on the way out of this screen — it writes coldroom_overflow
    // explicitly either way, so no stale flag rides along with a cell and hits
    // the pallets_overflow_has_no_cell CHECK.
    await withTimeout(updateWithVersion('pallets',
      { col: 'pallet_id', value: pallet.pallet_id },
      pallet.version,
      { ...placementPatch(destRoomId, dest), update_reason: pallet.coldroom_grid ? 'coldroom cell move' : 'coldroom off-grid placement' },
    ), 'Coldroom: place off-grid pallet')
  } catch (e) {
    if (isStaleWriteError(e)) {
      throw new ValidationError('This pallet was just updated by someone else. Refresh and try again.')
    }
    // 23505 / 23P01 from the unique index or the bin-occupancy trigger.
    const friendly = coldroomCellCollisionMessage(e)
    if (friendly) throw new ValidationError(friendly)
    throw e
  }
}

// `pallet` is NOT nullable: the mount site resolves it and renders nothing when
// the row has gone (someone else moved it while the list was on screen). A
// nullable prop meant an optional chain on every read and a "no longer on this
// list" branch inside the mutation, for a sheet that should not have opened.
//
// Same irreducible-JSX-orchestration shape as Rebin's PlaceBinModal and the
// TransferBinsModal it sits beside — a room picker, a CellPicker, and a
// version-guarded write — and the complexity is in the markup, not the logic.
// Four rounds of real reduction already came out of it: the occupancy maths
// went to src/lib/coldroomOccupancy.ts, the tile decision to describeCell, the
// room tabs to <DestinationRoomTabs/>, and the write to placeOffGridPallet
// above. What is left is the branching a form needs to render itself, and its
// behaviour is pinned by 10 assertions in the sibling test rather than by this
// number.
// NOTE — the prose is on its own lines ABOVE the bare directive on purpose:
// trailing text on the directive line parses as issue kinds (#2243).
// fallow-ignore-next-line complexity
export default function PlacePalletModal({ pallet, rooms, onClose, onSuccess, initialRoomId, initialCell, note }: {
  pallet:    PlaceablePallet
  rooms:     RoomTab[]
  /** From-production mode: the room on screen and, when an empty cell was
   *  tapped, that cell, already picked. */
  initialRoomId?: string | null
  initialCell?:   string | null
  /** One line above the picker (the loading-area variety note, M2). */
  note?:     string | null
  onClose:   () => void
  onSuccess: () => void
}) {
  const qc = useQueryClient()
  const { data: me } = useCurrentUser()
  const fromProduction = pallet.status === 'IN_PRODUCTION'
  const [destRoomId, setDestRoomId] = useState<string>(
    pallet.coldroom_id ?? (initialRoomId && rooms.some(r => r.coldroom_id === initialRoomId) ? initialRoomId : rooms[0]?.coldroom_id ?? ''))
  const [dest, setDest] = useState<ColdroomPlacement | null>(
    initialCell ? { kind: 'cell', cell: initialCell } : null)
  const [error, setError] = useState<string | null>(null)
  // The version the write matches on. A stale-write refusal means it is old, so
  // onError re-reads it: Retry must not resend the same version and fail again.
  const versionRef = useRef(pallet.version)
  const destCell = dest?.kind === 'cell' ? dest.cell : ''

  const place = useMutation({
    mutationFn: async () => {
      if (!destRoomId) throw new ValidationError('Pick a destination coldroom')
      if (!dest)       throw new ValidationError('Pick a cell, or the overflow')
      await placeOffGridPallet({ ...pallet, version: versionRef.current }, destRoomId, dest, me?.id)
    },
    onSuccess: () => {
      if (fromProduction) {
        qc.invalidateQueries({ queryKey: ['pallets'] })
        qc.invalidateQueries({ queryKey: ['pallet-detail', pallet.pallet_id] })
      }
      qc.invalidateQueries({ queryKey: ['coldroom-grid'] })
      qc.invalidateQueries({ queryKey: ['coldroom-occupied'] })
      qc.invalidateQueries({ queryKey: ['coldroom-counts'] })
      onSuccess()
    },
    onError: async (e: Error) => {
      setError(e.message)
      qc.invalidateQueries({ queryKey: ['pallets'] })
      qc.invalidateQueries({ queryKey: ['pallet-detail', pallet.pallet_id] })
      try {
        const { data, error: readError } = await withReadTimeout(
          supabase.from('pallets').select('version, status').eq('pallet_id', pallet.pallet_id).maybeSingle(),
          'Coldroom: re-read pallet version')
        if (readError) return
        const fresh = data as { version: number; status: string } | null
        if (!fresh) return
        // Another operator already placed it: keep the old version so Retry
        // stays refused rather than overwriting their placement.
        if (fromProduction && fresh.status !== 'IN_PRODUCTION') {
          setError('This pallet is no longer in production. Someone else placed it. Close this sheet and check the pallet.')
          return
        }
        versionRef.current = fresh.version
      } catch {
        // The re-read failed: the error above stands and Retry reuses the old version.
      }
    },
  })

  const fromRoomCode = rooms.find(r => r.coldroom_id === pallet.coldroom_id)?.code ?? '—'
  const fromWhere = fromProduction ? 'in production' : pallet.coldroom_grid ? `cell ${pallet.coldroom_grid}`
    : pallet.coldroom_overflow ? OVERFLOW_LABEL.toLowerCase() : 'no cell recorded'
  const verb = pallet.coldroom_grid ? 'Move' : 'Place'

  return (
    <Modal
      title={
        <span className="flex items-center gap-2">
          <Layers size={16} className="text-amber-400 shrink-0" />
          <span className="truncate">{verb} {pallet.pallet_id}</span>
        </span>
      }
      onClose={onClose}
      snapshot={`${destRoomId}|${dest?.kind ?? ''}|${destCell}`}
      onSubmit={() => place.mutate()}
      isPending={place.isPending}
      submitDisabled={!destRoomId || !placementIsComplete(dest)}
      submitLabel={pallet.coldroom_grid ? `Move to ${placementLabel(dest)}` : `Place at ${placementLabel(dest)}`}
      submitPendingLabel={pallet.coldroom_grid ? 'Moving…' : 'Placing…'}
      // Closing this sheet is not confirmation that the pallet moved — the
      // overlay is. It names the destination in its tick, because "Saved" on a
      // placement sheet leaves the operator to go and look at the grid to find
      // out WHERE the app thinks the pallet is now.
      overlay={{
        isPending: place.isPending,
        isSuccess: place.isSuccess,
        isError:   place.isError,
        error:     place.error,
        pendingLabel: pallet.coldroom_grid ? 'Moving…' : 'Placing…',
        successLabel: `${pallet.coldroom_grid ? 'Moved to' : 'Placed at'} ${placementLabel(dest)}`,
        onRetry:   () => place.mutate(),
      }}>
      <div className="space-y-3">
        <div className="bg-surface/40 rounded-lg p-3 text-xs">
          <p className="text-muted uppercase tracking-wider mb-1">Currently</p>
          <p className="font-mono text-amber-300">{fromProduction ? fromWhere : `${fromRoomCode} / ${fromWhere}`}</p>
        </div>
        {note && <p className="text-xs text-amber-200">{note}</p>}

        <DestinationRoomTabs rooms={rooms} value={destRoomId}
          onPick={id => { setDestRoomId(id); setDest(null) }} />

        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted mb-1.5">Destination</p>
          {/* The overflow stays on offer even here. Someone correcting a
              no-cell record is usually saying "it IS in the aisle" — refusing
              them that answer would push them back to picking a cell the
              pallet is not on, which is worse than no cell at all. */}
          <CellPicker
            mode="pallet"
            coldroomId={destRoomId || null}
            value={destCell}
            onChange={cell => setDest({ kind: 'cell', cell })}
            excludePalletId={pallet.pallet_id}
            allowOverflow
            overflowPicked={dest?.kind === 'overflow'}
            onPickOverflow={() => setDest({ kind: 'overflow' })}
          />
        </div>

        {error && (
          <div className="rounded-lg border border-red-700 bg-red-900/40 p-3 text-xs text-red-200 whitespace-pre-line">
            {error}
          </div>
        )}
      </div>
    </Modal>
  )
}
